import React, { useEffect, useState } from "react";
import { Container } from "reactstrap";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/CommonElements/PageTitle";
import { TableC } from "@/CommonElements/TableC";
import { useLocalizedText } from "@utils/hooks/useLocalizedText";
import { PrinterIcon, ViewIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { SelectCountry } from "@Component/Form/SelectCountry/SelectCountry";
import { useGetPilgrimsQuery } from "@services/piglgrims";
import { useDebouncedSearch } from "@utils/hooks/useDebouncedSearch";
import { LabelValue } from "@/Component/Value";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { PILGRIM_STATUS } from "@/utils/constants";
import { Tag } from "antd";
import PilgrimPrintSheet from "./PilgrimsDetails/PilgrimPrintSheet";

const Pilgrims = () => {
  const { t } = useTranslation();
  const { getLocalizedText, getLocalizedNumber } = useLocalizedText();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [printPilgrimId, setPrintPilgrimId] = useState(null);
  const [printRequestKey, setPrintRequestKey] = useState(null);

  // Use debounced search hook
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  const { data: pilgrimsRes, isLoading } = useGetPilgrimsQuery({
    page,
    per_page: 10,
    search: debouncedValue,
    ...filters,
  });

  // Reset page to 1 when filters or search change
  useEffect(() => {
    setPage(1);
  }, [debouncedValue, filters]);

  const columns = [
    {
      name: t("PILGRIM_NUMBER"),
      key: "border_number",
      render: (value, rowData) => (
        <LabelValue
          value={value}
          copyValue={value}
          buttonIconSize={12}
          renderValue={() => (
            <Link className="fw-bold" to={`${rowData?.id}/view`}>
              {getLocalizedNumber(value ?? "-")}
            </Link>
          )}
        />
      ),
    },
    {
      name: t("PILGRIM_NAME"),
      key: "name",
    },
    {
      name: t("PASSPORT_NUMBER"),
      key: "passport_number",
      render: (value, rowData) => (
        <LabelValue value={value} copyValue={value} buttonIconSize={12} />
      ),
    },
    {
      name: t("NATIONALITY"),
      key: "nationality_id",
      render: (value) => <SelectCountry.Display value={value} />,
    },
    {
      name: t("GENDER"),
      key: "gender",
      render: (value) => getLocalizedText(value?.ar ?? "-", value?.en ?? "-"),
    },
    { name: t("PILGRIM_TYPE"), key: "haj_type" },
    {
      name: t("PILGRIM_STATUS"),
      key: "pilgrim_status",
      render: (value) => <Tag color={value?.color ?? "red"}>{getLocalizedText(value?.label_ar ?? "-", value?.label_en ?? "-")}</Tag>,
    },
    {
      name: t("CAMPAIGN_OR_GROUP_NUMBER"),
      key: "campaign_or_group_id",
    },
    {
      name: t("ENTRY_PORT"),
      key: "entry_port_id",
      render: (value) => {

        return <SelectCachedOptions.Display
          value={value} campareBy="id"
          optionKey="ports"
          render={(option) => getLocalizedText(option?.name_ar, option?.name_en) || option?.name} />
      },
      visible: false,
    },
    {
      name: t("ACCOMMODATION_STATUS"),
      key: "is_accommodated",
      render: (value) => {
        const is_accommodated = !!(+value);

        return <Tag color={is_accommodated ? "success" : "error"}>{is_accommodated ? t("ACCOMMODATED") : t("NOT_ACCOMMODATED")}</Tag>
      },
      visible: false,
    },
    {
      name: t("MAKKAH_SERVICE_CENTER_NUMBER"),
      key: "makkah_service_center_id",
      render: (value) => <SelectCachedOptions.Display
        value={value} campareBy="id"
        optionKey="service_centers"
        render={(option) => option?.name} />

    },
    {
      name: t("MADINAH_SERVICE_CENTER_NUMBER"),
      key: "madinah_service_center_id",
      render: (value) => <SelectCachedOptions.Display
        value={value} campareBy="id"
        optionKey="service_centers"
        render={(option) => option?.name} />
    },
    {
      name: t("BAG_NUMBER"),
      key: "bag_number",

    },
    {
      name: t("OUT_PORT"),
      key: "exit_port_id",
      render: (value) => {

        return <SelectCachedOptions.Display
          value={value} campareBy="id"
          optionKey="ports"
          render={(option) => getLocalizedText(option?.name_ar, option?.name_en) || option?.name} />
      },
      visible: false,
    },
    { name: t("ACTIONS"), key: "actions", filterable: false },
  ];

  const filterFields = [
    {
      colSize: { md: 4 },
      name: "name",
      label: t("أسم الحاج"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "nationality_id",
      label: t("الجنسية"),
      type: "select",
      optionKey: "nationalities",
      options: (n) =>
      ({
        value: n.id,
        label: <SelectCountry.Display value={n.id} />,
      } || []),
    },
    {
      colSize: { md: 4 },
      name: "mission_organizer_id",
      label: t("المنظم"),
      type: "select",
      placeholder: t("المنظم"),
      optionKey: "missions",
      options: (m) =>
      ({
        value: m.id,
        label: (
          <LabelValue.Value
            value={[m.id, getLocalizedText(m.name_ar, m.name_en)].join(" - ")}
          />
        ),
      } || []),
    },
    {
      colSize: { md: 4 },
      name: "campaign_number", //group_number
      label: t("رقم الحملة / المجموعة"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "passport_number",
      label: t("رقم الجواز"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "visa_number",
      label: t("رقم التأشيرة"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "ad_embassy_id",
      label: t("مصدر التأشيرة"),
      type: "select",
      optionKey: "embassies",
      options: (a) =>
      ({
        value: a.id,
        label: (
          <LabelValue.Value value={getLocalizedText(a.name_ar, a.name_en)} />
        ),
      } || []),
    },
    {
      colSize: { md: 4 },
      name: "service_center_id",
      label: t("مركز الخدمة"),
      type: "select",
      optionKey: "service_centers",
      options: (s) =>
      ({
        value: s.id,
        label: <LabelValue.Value value={s.name} />,
      } || []),
    },
    {
      colSize: { md: 4 },
      name: "entry_port_id",
      label: t("منفذ الدخول"),
      type: "select",
      optionKey: "ports",
      options: (p) => ({
        value: p.id,
        label: <LabelValue.Value value={getLocalizedText(p?.name_ar, p?.name_en) || p?.name} />,
      }),
    },
    {
      colSize: { md: 4 },
      name: "nusuk_card",
      label: t("رقم بطاقة نسك"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "haj_type",
      label: t("نوع الحاج"),
      type: "select",
      options: [
        { label: t("HAJ"), value: "haj" },
        { label: t("MISSION_MEMBER"), value: "mission" },
      ],
    },
    {
      colSize: { md: 4 },
      name: "pilgrim_status",
      label: t("حالة الحاج"),
      type: "select",
      options: PILGRIM_STATUS.map(status => ({
        label: getLocalizedText(status.label_ar, status.label_en),
        value: status.key,
      })),
    },
    {
      colSize: { md: 4 },
      name: "flight_number",
      label: t("رقم الرحلة"),
      type: "input",
    },
    {
      colSize: { md: 4 },
      name: "ad_camp_id",
      label: t("رقم المخيم"),
      type: "select",
      optionKey: "camps",
      options: (c) => ({
        value: c.id,
        label: <LabelValue.Value value={[c.id, c.camp_no, `(${c.camp_street_to_arafat})`].join(" - ")} />,
      }),
    },
    {
      colSize: { md: 4 },
      name: "bag_number",
      label: t("رقم الحقيبة"),
      type: "text"
    },
    {
      colSize: { md: 4 },
      name: "is_accommodated",
      label: t("حالة التسكين مكه"),
      type: "select",
      options: [
        { label: t("ACCOMMODATED"), value: "1" },
        { label: t("NOT_ACCOMMODATED"), value: "0" },
      ],
    },
    {
      colSize: { md: 4 },
      name: "accommodated_in_madina",
      label: t("حالة التسكين في المدينة"),
      type: "select",
      options: [
        { label: t("ACCOMMODATED"), value: "1" },
        { label: t("NOT_ACCOMMODATED"), value: "0" },
      ],
    },
  ];

  const pilgrims = pilgrimsRes?.data?.data.data ?? [];

  const handlePrint = (pilgrimId) => {
    setPrintPilgrimId(pilgrimId);
    setPrintRequestKey(`${pilgrimId}-${Date.now()}`);
  };

  const getActions = (rowData) => {
    return [
      {
        key: "view",
        label: (
          <Link
            to={`${rowData.id}/view`}
            className="d-flex align-items-center gap-2 text-decoration-none"
          >
            <HugeiconsIcon size={16} icon={ViewIcon} className="flex-shrink-0" />
            <span>{t("VIEW")}</span>
          </Link>
        ),
      },
      {
        key: "print",
        label: (
          <button
            type="button"
            className="d-flex align-items-center gap-2 btn btn-link p-0 text-decoration-none"
            onClick={() => handlePrint(rowData.id)}
          >
            <HugeiconsIcon size={16} icon={PrinterIcon} className="flex-shrink-0" />
            <span>{t("PRINT")}</span>
          </button>
        ),
      },
    ];
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleFilterApply = (filterData) => {
    setFilters(filterData);
  };

  const handleFilterReset = () => {
    setFilters({});
  };

  const meta = pilgrimsRes?.data?.data?.meta;
  return (
    <div>
      <PageHeader title={t("PILGRIMS")} sticky={false} />
      <Container fluid>
        <TableC
          // basic
          data={pilgrims}
          columns={columns}
          loading={isLoading}
          // search
          searchValue={searchValue}
          onSearchChange={handleSearchChange}
          // pagination
          pagination={{
            current: page,
            total: meta?.total ?? 0,
            pageSize: meta?.per_page ?? 10,
            onChange: (page) => {
              setPage(page);
            },
          }}
          // filter
          filterFields={filterFields}
          filters={filters}
          onFilterApply={handleFilterApply}
          onFilterReset={handleFilterReset}
          // actions
          actions={getActions}
        />
        <PilgrimPrintSheet
          pilgrimId={printPilgrimId}
          printRequestKey={printRequestKey}
          onPrinted={() => {
            setPrintPilgrimId(null);
            setPrintRequestKey(null);
          }}
        />
      </Container>
    </div>
  );
};

export default Pilgrims;
